/**
 * PromoterDashboardChart.tsx - Promoter Performance Analytics Chart
 * 
 * This component displays interactive charts showing promoter performance
 * over time with trends, comparisons, and detailed analytics.
 * 
 * Key Features:
 * - Time-series performance charts for individual promoters
 * - Interactive charts with hover details and tooltips
 * - Multiple chart types (line, bar, area) for different metrics
 * - Time period selection and filtering
 * - Comparative analysis with other promoters
 * - Export capabilities for chart data and images
 * 
 * Chart Types:
 * - Line Chart: Performance trends over time
 * - Bar Chart: Daily/weekly donation amounts
 * - Area Chart: Cumulative progress visualization
 * - Comparison Chart: Performance vs other promoters
 * 
 * Metrics Visualized:
 * - Daily donation amounts attributed to promoter
 * - Cumulative progress toward goal
 * - Donation frequency and timing patterns
 * - Performance ranking changes over time
 * - Goal achievement milestones
 * 
 * Interactive Features:
 * - Hover tooltips with detailed information
 * - Time period selection (7d, 30d, 90d, all time)
 * - Zoom and pan for detailed analysis
 * - Data point highlighting and selection
 * - Export to PNG/SVG formats
 * 
 * Props:
 * - promoterId: string - ID of promoter for chart data
 * - chartType?: string - Type of chart to display
 * - timePeriod?: string - Time period for data
 * 
 * Dependencies: Chart library, date utilities, performance calculations
 */

import React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { TrendingUp, BarChart3 } from 'lucide-react';
import { fundraiserStore } from '../../stores/FundraiserStore';

const ChartContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  margin: 32px 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const ChartTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChartWrapper = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 24px 24px 16px 60px;
  height: 350px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  width: 100%;
`;

const AreaChart = styled.svg`
  width: 100%;
  height: 100%;
`;

const ChartPath = styled.path`
  fill: url(#gradient);
  stroke: #10b981;
  stroke-width: 2;
`;

const EmptyChartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
`;

const EmptyChartIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 20px;
`;

const EmptyChartTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyChartText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

interface PromoterDashboardChartProps {
  promoterId: string;
  fundraiserId: string;
}

export const PromoterDashboardChart: React.FC<PromoterDashboardChartProps> = observer(({
  promoterId,
  fundraiserId
}) => {
  const formatShortDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Generate daily donation data for this specific promoter
  const generatePromoterDailyData = () => {
    const allDonations = fundraiserStore.getPromoterDonations(promoterId, fundraiserId);
    
    if (allDonations.length === 0) {
      return [];
    }
    
    // Get date range from first donation to today
    const sortedDonations = allDonations.slice().sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const firstDonationDate = sortedDonations[0].timestamp;
    const today = new Date();
    
    // Calculate days from first donation to today, minimum 7 days for chart
    const daysDiff = Math.ceil((today.getTime() - firstDonationDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysToShow = Math.max(daysDiff + 1, 7);
    
    const startDate = new Date(firstDonationDate);
    const endDate = new Date(today);
    
    // Create daily buckets
    const dailyBuckets = new Map<string, number>();
    
    // Initialize all days with 0
    for (let i = 0; i < daysToShow; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateKey = currentDate.toISOString().split('T')[0];
      dailyBuckets.set(dateKey, 0);
    }
    
    // Add actual donations to the buckets
    allDonations.forEach(donation => {
      const dateKey = donation.timestamp.toISOString().split('T')[0];
      if (dailyBuckets.has(dateKey)) {
        const currentAmount = dailyBuckets.get(dateKey) || 0;
        dailyBuckets.set(dateKey, currentAmount + donation.amount);
      }
    });
    
    // Convert to array format
    const data: { date: Date; amount: number }[] = [];
    for (let i = 0; i < daysToShow; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dateKey = currentDate.toISOString().split('T')[0];
      
      data.push({
        date: new Date(currentDate),
        amount: dailyBuckets.get(dateKey) || 0
      });
    }
    
    return data;
  };

  const dailyData = generatePromoterDailyData();
  const maxAmount = Math.max(...dailyData.map(d => d.amount), 1);
  const totalRaised = dailyData.reduce((sum, d) => sum + d.amount, 0);
  const donationCount = fundraiserStore.getPromoterDonations(promoterId, fundraiserId).length;
  const hasNoDonations = totalRaised === 0 || donationCount === 0;

  const createPath = () => {
    if (dailyData.length === 0 || hasNoDonations) return { areaPath: '', points: [] };
    
    const chartWidth = 800;
    const chartHeight = 250;
    const padding = 40;
    const plotHeight = chartHeight - 100; // Available height for plotting
    const baselineY = chartHeight - 60; // Bottom of the chart
    
    const points = dailyData.map((d, i) => {
      const x = 60 + (i * (chartWidth - 120)) / (dailyData.length - 1);
      const normalizedAmount = d.amount / maxAmount;
      const y = baselineY - (normalizedAmount * plotHeight);
      return { x, y };
    });
    
    // Create smooth line path
    let pathData = '';
    if (points.length > 0) {
      pathData = `M${points[0].x},${baselineY}`;
      
      for (let i = 1; i < points.length; i++) {
        pathData += ` L${points[i].x},${points[i].y}`;
      }
    }
    
    // Create area path
    const areaPath = points.length > 0 
      ? `M${points[0].x},${baselineY} ${points.map((p, i) => `L${p.x},${p.y}`).join(' ')} L${points[points.length - 1].x},${baselineY} L${60},${baselineY} Z`
      : '';
    
    return { areaPath, points };
  };

  const { areaPath } = createPath();

  return (
    <ChartContainer>
      <ChartTitle>
        <TrendingUp size={22} />
        Daily Donations
      </ChartTitle>
      <ChartWrapper>
        {hasNoDonations ? (
          <EmptyChartState>
            <EmptyChartIcon>
              <BarChart3 size={40} />
            </EmptyChartIcon>
            <EmptyChartTitle>No Donations Yet</EmptyChartTitle>
            <EmptyChartText>
              Donation activity will appear here once the<br />
              first donation is received.
            </EmptyChartText>
          </EmptyChartState>
        ) : (
          <AreaChart viewBox="0 0 800 250" preserveAspectRatio="none">
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 0.3 }} />
                <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 0.05 }} />
              </linearGradient>
            </defs>
            
            {areaPath && <ChartPath d={areaPath} />}
            
            {/* Date labels below the chart */}
            {dailyData.map((dataPoint, i) => {
              // Show dates at regular intervals based on data length
              const interval = Math.max(1, Math.floor(dailyData.length / 6));
              if (i % interval === 0 || i === dailyData.length - 1) {
                const x = 60 + (i * 680) / (dailyData.length - 1);
                return (
                  <text 
                    key={i}
                    x={x} 
                    y={235} 
                    textAnchor="middle" 
                    fontSize="12" 
                    fill="#64748b"
                    fontWeight="500"
                  >
                    {formatShortDate(dataPoint.date)}
                  </text>
                );
              }
              return null;
            })}
            
            {/* Y-axis labels */}
            <text x="50" y="50" textAnchor="end" fontSize="12" fill="#64748b" fontWeight="500">
              ${Math.round(maxAmount).toLocaleString()}
            </text>
            <text x="50" y="150" textAnchor="end" fontSize="12" fill="#64748b" fontWeight="500">
              ${Math.round(maxAmount / 2).toLocaleString()}
            </text>
            <text x="50" y="200" textAnchor="end" fontSize="12" fill="#64748b" fontWeight="500">
              $0
            </text>
          </AreaChart>
        )}
      </ChartWrapper>
    </ChartContainer>
  );
});